import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TrackerLayout } from "./TrackerLayout";
import { getJson } from "@/lib/http";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { NewFeatureBadge } from "@/components/tracker/NewFeatureBadge";
import { Loader2, Save, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { REJECTION_TYPE_DISPLAY_ORDER, type RejectionType } from "@shared/rejection-types";
type NotesRequirement = "NONE" | "OPTIONAL" | "REQUIRED";

interface ReasonCode {
    code: string;
    label: string;
    description: string | null;
    appliesTo: RejectionType[];
    notesRequirement: NotesRequirement;
    sortOrder: number;
    isActive: boolean;
}

interface DraftReason {
    label: string;
    description: string;
    appliesTo: RejectionType[];
    notesRequirement: NotesRequirement;
    sortOrder: number;
    isActive: boolean;
}

const REJECTION_OPTION_LABELS: Record<RejectionType, string> = {
    PHOTO_ONLY: "Photo retake only",
    REVISIT: "Physical revisit",
    REVISIT_WITH_SIGNOFF: "Revisit + manager sign-off",
    ADVISORY_ONLY: "Advisory only (no operator work)",
};

const REJECTION_OPTIONS: Array<{ value: RejectionType; label: string }> =
    REJECTION_TYPE_DISPLAY_ORDER.map((value) => ({
        value,
        label: REJECTION_OPTION_LABELS[value],
    }));

function normalizeCode(input: string): string {
    return input.trim().toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");
}

export default function RejectionReasonSettingsTab() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const [drafts, setDrafts] = useState<Record<string, DraftReason>>({});
    const [newReason, setNewReason] = useState({
        code: "",
        label: "",
        description: "",
        appliesTo: ["PHOTO_ONLY", "REVISIT", "REVISIT_WITH_SIGNOFF", "ADVISORY_ONLY"] as RejectionType[],
        notesRequirement: "OPTIONAL" as NotesRequirement,
        sortOrder: 0,
        isActive: true,
    });

    const { data, isLoading, refetch, isRefetching } = useQuery<{ success: boolean; data: ReasonCode[] }>({
        queryKey: ["/api/review/rejection-reasons"],
        queryFn: async () => getJson<{ success: boolean; data: ReasonCode[] }>("/api/review/rejection-reasons?includeInactive=1"),
    });

    const reasons = data?.data || [];

    useEffect(() => {
        if (!reasons.length) return;
        setDrafts((prev) => {
            const next = { ...prev };
            reasons.forEach((reason) => {
                if (next[reason.code]) return;
                next[reason.code] = {
                    label: reason.label,
                    description: reason.description || "",
                    appliesTo: reason.appliesTo,
                    notesRequirement: reason.notesRequirement,
                    sortOrder: reason.sortOrder,
                    isActive: reason.isActive,
                };
            });
            return next;
        });
    }, [reasons]);

    const createMutation = useMutation({
        mutationFn: async () => {
            const payload = {
                code: normalizeCode(newReason.code),
                label: newReason.label.trim(),
                description: newReason.description.trim() || undefined,
                appliesTo: newReason.appliesTo,
                notesRequirement: newReason.notesRequirement,
                sortOrder: Number(newReason.sortOrder || 0),
                isActive: newReason.isActive,
            };

            const response = await fetch("/api/review/rejection-reasons", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result?.error || "Failed to create reason");
            }
            return result;
        },
        onSuccess: () => {
            toast({ title: "Reason created" });
            setNewReason({
                code: "",
                label: "",
                description: "",
                appliesTo: ["PHOTO_ONLY", "REVISIT", "REVISIT_WITH_SIGNOFF"],
                notesRequirement: "OPTIONAL",
                sortOrder: 0,
                isActive: true,
            });
            queryClient.invalidateQueries({ queryKey: ["/api/review/rejection-reasons"] });
        },
        onError: (error: Error) => {
            toast({ title: "Create failed", description: error.message, variant: "destructive" });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ code, draft }: { code: string; draft: DraftReason }) => {
            const response = await fetch(`/api/review/rejection-reasons/${code}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    label: draft.label.trim(),
                    description: draft.description.trim() || undefined,
                    appliesTo: draft.appliesTo,
                    notesRequirement: draft.notesRequirement,
                    sortOrder: Number(draft.sortOrder || 0),
                    isActive: draft.isActive,
                }),
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result?.error || "Failed to update reason");
            }
            return result;
        },
        onSuccess: () => {
            toast({ title: "Reason updated" });
            queryClient.invalidateQueries({ queryKey: ["/api/review/rejection-reasons"] });
        },
        onError: (error: Error) => {
            toast({ title: "Update failed", description: error.message, variant: "destructive" });
        },
    });

    const sortedReasons = useMemo(
        () => [...reasons].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
        [reasons]
    );

    const toggleAppliesTo = (draft: DraftReason, value: RejectionType): RejectionType[] => {
        if (draft.appliesTo.includes(value)) {
            return draft.appliesTo.filter((item) => item !== value);
        }
        return [...draft.appliesTo, value];
    };

    return (
        <TrackerLayout title="Rejection Reason Settings" onRefresh={() => refetch()} isRefreshing={isRefetching}>
            <div className="space-y-6 max-w-7xl mx-auto">
                <Card>
                    <CardHeader>
                        <CardTitle>Create Reason Chip</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Input
                                placeholder="Code (e.g. BLURRY)"
                                value={newReason.code}
                                onChange={(e) => setNewReason((prev) => ({ ...prev, code: e.target.value }))}
                            />
                            <Input
                                placeholder="Label"
                                value={newReason.label}
                                onChange={(e) => setNewReason((prev) => ({ ...prev, label: e.target.value }))}
                            />
                            <Input
                                type="number"
                                placeholder="Sort order"
                                value={String(newReason.sortOrder)}
                                onChange={(e) => setNewReason((prev) => ({ ...prev, sortOrder: Number(e.target.value || 0) }))}
                            />
                        </div>
                        <Textarea
                            placeholder="Description (optional)"
                            value={newReason.description}
                            onChange={(e) => setNewReason((prev) => ({ ...prev, description: e.target.value }))}
                            rows={2}
                        />
                        <div className="flex flex-wrap gap-4">
                            {REJECTION_OPTIONS.map((option) => (
                                <label key={option.value} className="flex items-center gap-2 text-sm">
                                    <Checkbox
                                        checked={newReason.appliesTo.includes(option.value)}
                                        onCheckedChange={() => setNewReason((prev) => ({
                                            ...prev,
                                            appliesTo: toggleAppliesTo({
                                                label: prev.label,
                                                description: prev.description,
                                                appliesTo: prev.appliesTo,
                                                notesRequirement: prev.notesRequirement,
                                                sortOrder: prev.sortOrder,
                                                isActive: prev.isActive,
                                            }, option.value),
                                        }))}
                                    />
                                    <span className="inline-flex items-center gap-1.5">
                                        {option.label}
                                        {option.value === "ADVISORY_ONLY" && <NewFeatureBadge />}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="flex items-center gap-3">
                            <Select
                                value={newReason.notesRequirement}
                                onValueChange={(value) => setNewReason((prev) => ({ ...prev, notesRequirement: value as NotesRequirement }))}
                            >
                                <SelectTrigger className="w-[220px]">
                                    <SelectValue placeholder="Notes requirement" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="NONE">Notes: none</SelectItem>
                                    <SelectItem value="OPTIONAL">Notes: optional</SelectItem>
                                    <SelectItem value="REQUIRED">Notes: required</SelectItem>
                                </SelectContent>
                            </Select>
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    checked={newReason.isActive}
                                    onCheckedChange={(checked) => setNewReason((prev) => ({ ...prev, isActive: Boolean(checked) }))}
                                />
                                Active
                            </label>
                            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
                                {createMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                                Create
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Existing Reason Chips</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : sortedReasons.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No rejection reasons configured yet.</p>
                        ) : (
                            sortedReasons.map((reason) => {
                                const draft = drafts[reason.code];
                                if (!draft) return null;
                                return (
                                    <div key={reason.code} className="border rounded-lg p-4 space-y-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="font-mono">{reason.code}</Badge>
                                                {!draft.isActive && <Badge variant="secondary">Inactive</Badge>}
                                            </div>
                                            <Button
                                                size="sm"
                                                onClick={() => updateMutation.mutate({ code: reason.code, draft })}
                                                disabled={updateMutation.isPending || draft.appliesTo.length === 0 || !draft.label.trim()}
                                            >
                                                {updateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                                Save
                                            </Button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <Input
                                                value={draft.label}
                                                onChange={(e) => setDrafts((prev) => ({
                                                    ...prev,
                                                    [reason.code]: { ...draft, label: e.target.value },
                                                }))}
                                            />
                                            <Input
                                                type="number"
                                                value={String(draft.sortOrder)}
                                                onChange={(e) => setDrafts((prev) => ({
                                                    ...prev,
                                                    [reason.code]: { ...draft, sortOrder: Number(e.target.value || 0) },
                                                }))}
                                            />
                                            <Select
                                                value={draft.notesRequirement}
                                                onValueChange={(value) => setDrafts((prev) => ({
                                                    ...prev,
                                                    [reason.code]: { ...draft, notesRequirement: value as NotesRequirement },
                                                }))}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Notes requirement" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="NONE">Notes: none</SelectItem>
                                                    <SelectItem value="OPTIONAL">Notes: optional</SelectItem>
                                                    <SelectItem value="REQUIRED">Notes: required</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <Textarea
                                            rows={2}
                                            value={draft.description}
                                            onChange={(e) => setDrafts((prev) => ({
                                                ...prev,
                                                [reason.code]: { ...draft, description: e.target.value },
                                            }))}
                                        />

                                        <div className="flex flex-wrap gap-4">
                                            {REJECTION_OPTIONS.map((option) => (
                                                <label key={`${reason.code}-${option.value}`} className="flex items-center gap-2 text-sm">
                                                    <Checkbox
                                                        checked={draft.appliesTo.includes(option.value)}
                                                        onCheckedChange={() => setDrafts((prev) => ({
                                                            ...prev,
                                                            [reason.code]: {
                                                                ...draft,
                                                                appliesTo: toggleAppliesTo(draft, option.value),
                                                            },
                                                        }))}
                                                    />
                                                    <span className="inline-flex items-center gap-1.5">
                                                        {option.label}
                                                        {option.value === "ADVISORY_ONLY" && <NewFeatureBadge />}
                                                    </span>
                                                </label>
                                            ))}
                                            <label className="flex items-center gap-2 text-sm">
                                                <Checkbox
                                                    checked={draft.isActive}
                                                    onCheckedChange={(checked) => setDrafts((prev) => ({
                                                        ...prev,
                                                        [reason.code]: { ...draft, isActive: Boolean(checked) },
                                                    }))}
                                                />
                                                Active
                                            </label>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </CardContent>
                </Card>
            </div>
        </TrackerLayout>
    );
}
